import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import {
    Card,
    Badge,
    Row,
    Col,
    Button,
    Spinner,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
} from "reactstrap";
import Head from "../../layout/head/Head";
import {
    BlockBetween,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    Icon,
} from "../../components/Component";
import {
    DownloadOutlined,
    EyeOutlined,
    UploadOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import {
    TableColumnsType,
    Table,
    Timeline,
    Collapse as AntCollapse,
    Flex,
    Spin,
    Popover,
    InputNumber,
    Modal as AntModal,
    Tooltip,
    Dropdown
} from "antd";
import inventoryCreation from "../../api/inventory/inventoryCreation";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import MaterialHandoverPDF from "./MaterialHandoverPDF";
import { useSelector } from "react-redux";
import {
    UtilizationTaskPanel,
    makeUtilizationTaskKey,
} from "./UtilizationTaskPanel";
const { Panel } = AntCollapse;

const downloadDocument = (filePath: string, fileName: string) => {
    try {
        fetch(filePath)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName || "document";
                link.style.display = "none";

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                const link = document.createElement("a");
                link.href = filePath;
                link.download = fileName || "document";
                link.target = "_blank";
                link.style.display = "none";

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    } catch (error) {
        window.open(filePath, "_blank");
    }
};

const FileViewDropdown = ({ files = [] }: { files: any[] }) => {
    if (!files.length) return null;

    return (
        <Dropdown
            trigger={["hover"]}
            placement="bottomRight"
            menu={{
                items: files.map((file, index) => ({
                    key: index,
                    label: (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "10px",
                                minWidth: "220px",
                            }}
                        >
                            {/* File name → VIEW */}
                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "160px",
                                    cursor: "pointer",
                                }}
                                title={file.fileName}
                                onClick={() => window.open(file.filePath, "_blank")}
                            >
                                {file.fileName}
                            </span>


                            {/* Actions */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                {/* View */}
                                <EyeOutlined
                                    style={{ color: "#4169E1", cursor: "pointer" }}
                                    title="View file"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(file.filePath, "_blank");
                                    }}
                                />

                                {/*  Download */}
                                <DownloadOutlined
                                    style={{ color: "#52c41a", cursor: "pointer" }}
                                    title="Download file"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        downloadDocument(
                                            file.filePath,
                                            file.fileName || "document"
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    ),
                })),
            }}
        >
            <span
                style={{
                    cursor: "pointer",
                    color: "#4169E1",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                }}
                title="View Documents"
            >
                <EyeOutlined />
            </span>
        </Dropdown>
    );
};


// File Upload Modal Component
const FileUploadModal = ({
    isVisible,
    onClose,
    handoverLineId,
    moduleFileType,
    uploadedFiles = [],
    onFilesUpload
}: any) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "No Files",
                text: "Please select at least one file to upload",
                confirmButtonText: "OK"
            });
            return;
        }

        setIsUploading(true);
        try {
            onFilesUpload(files, handoverLineId, moduleFileType);
            Swal.fire({
                icon: "success",
                title: "Files Uploaded",
                text: `${files.length} file(s) uploaded successfully`,
                confirmButtonText: "OK"
            });
            setFiles([]);
            onClose();
        } catch (error) {
            console.error("Upload error:", error);
            Swal.fire({
                icon: "error",
                title: "Upload Failed",
                text: "Failed to upload files. Please try again.",
                confirmButtonText: "OK"
            });
        } finally {
            setIsUploading(false);
        }
    };

    const getFileIcon = (fileName: string) => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(ext || '')) return '📄';
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext || '')) return '🖼️';
        if (['doc', 'docx'].includes(ext || '')) return '📝';
        if (['xls', 'xlsx'].includes(ext || '')) return '📊';
        return '📎';
    };

    return (
        <AntModal
            title={`Upload Files - ${moduleFileType === 'damaged' ? 'Damaged' : 'Returned'} Quantity`}
            open={isVisible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose} className="btn-dim btn btn-secondary me-2">
                    Cancel
                </Button>,
                <Button
                    key="upload"
                    color="primary"
                    onClick={handleUpload}
                    disabled={files.length === 0 || isUploading}
                >
                    {isUploading ? <Spinner size="sm" color="light" /> : "Upload Files"}
                </Button>
            ]}
            width={700}
        >
            <div className="p-3">
                <div className="mb-3">
                    <label className="form-label">Select Files</label>
                    <input
                        type="file"
                        className="form-control"
                        multiple
                        onChange={handleFileSelect}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    />
                    <small className="text-muted">Maximum file size: 10MB each</small>
                </div>

                {files.length > 0 && (
                    <div className="mb-3">
                        <label className="form-label">Selected Files ({files.length})</label>
                        <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {files.map((file, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="text-truncate" style={{ maxWidth: '70%' }}>
                                        {getFileIcon(file.name)} {file.name}
                                    </span>
                                    <Button
                                        size="sm"
                                        color="danger"
                                        onClick={() => removeFile(index)}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {uploadedFiles.length > 0 && (
                    <div className="mt-4">
                        <label className="form-label">Previously Uploaded Files ({uploadedFiles.length})</label>
                        <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <Row>
                                {uploadedFiles.map((file: any, index: number) => (
                                    <Col key={index} xs={12} md={6} className="mb-3">
                                        <div className="border rounded p-2 text-center" style={{ height: '100px' }}>
                                            <div className="mb-2" style={{ fontSize: '24px' }}>
                                                {getFileIcon(file.fileName || `File ${index + 1}`)}
                                            </div>
                                            <div className="small text-truncate mb-2" title={file.fileName || `File ${index + 1}`}>
                                                {file.fileName || `File ${index + 1}`}
                                            </div>
                                            <Button
                                                size="sm"
                                                className="w-100"
                                                onClick={() => window.open(file.filePath, '_blank')}
                                            >
                                                <EyeOutlined /> View
                                            </Button>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                )}
            </div>
        </AntModal>
    );
};

const MaterialHandoverListDetails = () => {
    const { data: currentUser } = useSelector((state: any) => state.currentUser);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fromList = location.state?.fromList || false;
    const [isLoading, setIsLoading] = useState(false);
    const [detailsData, setDetailsData] = useState<any>();
    const [isStrictMode, setIsStrictMode] = useState<boolean>(false);
    const [approverIds, setApproverIds] = useState<any>([]);
    const [showSubmitBtm, setShowSubmitBtm] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedQuantities, setEditedQuantities] = useState<
        Record<number, { utilized: number; returned: number; damaged: number }>
    >({});
    const [hasChanges, setHasChanges] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [selectedUploadData, setSelectedUploadData] = useState<any>(null);
    const [selectedFiles, setSelectedFiles] = useState<
        {
            file: File;
            handoverLineId: number;
            moduleFileType: "damaged" | "returned";
        }[]
    >([]);

    // Available handover-task options per line (picker source for the
    // utilization split). Keyed by handoverLineId.
    const [lineHandoverTaskOptions, setLineHandoverTaskOptions] = useState<
        Record<string, any[]>
    >({});
    // Per-line utilization task allocations, keyed by handoverLineId. Each
    // entry: { key, handoverTaskId, utilizedQty, returnQty, damagedQty }.
    const [lineTasks, setLineTasks] = useState<Record<string, any[]>>({});

    const updateTask = (lineKey: string | number, taskKey: string, changes: any) =>
        setLineTasks((prev) => ({
            ...prev,
            [lineKey]: (prev[lineKey] || []).map((t) =>
                t.key === taskKey ? { ...t, ...changes } : t
            ),
        }));

    const getLocalUploadedFiles = (
        handoverLineId: number,
        moduleFileType: "returned" | "damaged"
    ) => {
        return selectedFiles
            .filter(
                f =>
                    f.handoverLineId === handoverLineId &&
                    f.moduleFileType === moduleFileType
            )
            .map((f, index) => ({
                fileName: f.file.name,
                filePath: URL.createObjectURL(f.file),
                __local: true,
                key: `local-${index}`,
            }));
    };

    // Get uploaded files from dms data
    const getUploadedFiles = (handoverLineId: number, moduleFileType: string) => {
        if (!detailsData?.dms) return [];

        return detailsData.dms.filter((doc: any) =>
            doc.handoverLineId === handoverLineId &&
            doc.moduleFileType === moduleFileType
        );
    };

    const handleDeleteLocalFile = (
        handoverLineId: number,
        moduleFileType: "returned" | "damaged",
        fileName: string
    ) => {
        setSelectedFiles((prev) =>
            prev.filter(
                (f) =>
                    !(
                        f.handoverLineId === handoverLineId &&
                        f.moduleFileType === moduleFileType &&
                        f.file.name === fileName
                    )
            )
        );
    };

    const canUpdateHandover = detailsData?.header?.utilizationStatus === "Pending";

    const signatureFromApi = detailsData?.dms?.find(
        (doc: any) => doc.moduleFileType === "Signature"
    );

    useEffect(() => {
        if (id) {
            fetchHandoverDetails(id);
        }
    }, [id]);

    // Load handover-task options per line. Prefers any options already
    // attached to the line (line.handoverTasks/line.tasks) and falls back to
    // the demo stub until the real endpoint lands.
    useEffect(() => {
        const lines = detailsData?.lines || [];
        if (!lines.length) return;
        let cancelled = false;
        (async () => {
            const next: Record<string, any[]> = {};
            await Promise.all(
                lines.map(async (line: any) => {
                    const fromLine =
                        line?.handoverTasks ||
                        (Array.isArray(line?.tasks) &&
                        line.tasks.every((t: any) => t.handoverTaskId != null)
                            ? line.tasks
                            : null);
                    if (Array.isArray(fromLine) && fromLine.length > 0) {
                        next[line.handoverLineId] = fromLine;
                        return;
                    }
                    try {
                        const res: any =
                            await inventoryCreation.fetchHandoverLineTasksDemo(
                                line.handoverLineId
                            );
                        if (res?.status && Array.isArray(res.data)) {
                            next[line.handoverLineId] = res.data;
                        } else {
                            next[line.handoverLineId] = [];
                        }
                    } catch (err) {
                        next[line.handoverLineId] = [];
                    }
                })
            );
            if (!cancelled) setLineHandoverTaskOptions(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [detailsData?.lines]);

    // Build one editable row per available handover task option, hydrating
    // qty fields from any saved backend split so the user only edits qtys
    // (no Add Task button — tasks are pre-determined at the handover stage).
    useEffect(() => {
        if (!detailsData?.lines) return;
        const seeded: Record<string, any[]> = {};
        detailsData.lines.forEach((line: any) => {
            const options = lineHandoverTaskOptions[line.handoverLineId] || [];
            const backendTasks: any[] = Array.isArray(line?.tasks)
                ? line.tasks
                : [];
            seeded[line.handoverLineId] = options.map((opt: any) => {
                const existing = backendTasks.find(
                    (t: any) => t.handoverTaskId === opt.handoverTaskId
                );
                return {
                    key: makeUtilizationTaskKey(),
                    handoverTaskId: opt.handoverTaskId,
                    taskName: opt.taskName,
                    boqName: opt.boqName,
                    utilizedQty: existing?.utilizedQty,
                    returnQty: existing?.returnQty,
                    damagedQty: existing?.damagedQty,
                };
            });
        });
        setLineTasks(seeded);
    }, [detailsData?.lines, lineHandoverTaskOptions]);

    // When a line has task-level allocations, the header Utilized/Returned/
    // Damaged are sums of the task qtys. Keep editedQuantities in sync so the
    // existing submit logic picks up aggregated values without special-casing.
    useEffect(() => {
        if (!isEditMode) return;
        const lines = detailsData?.lines || [];
        if (!lines.length) return;
        setEditedQuantities((prev) => {
            const next = { ...prev };
            let changed = false;
            lines.forEach((line: any) => {
                const hasTaskLines =
                    Array.isArray(line?.materialHandoverTasks) &&
                    line.materialHandoverTasks.length > 0;
                if (!hasTaskLines) return;
                const stateForLine = lineTasks[line.handoverLineId] || [];
                const utilizedSum = stateForLine.reduce(
                    (acc: number, t: any) =>
                        acc + (t?.utilizedQty != null ? Number(t.utilizedQty) : 0),
                    0
                );
                const returnedSum = stateForLine.reduce(
                    (acc: number, t: any) =>
                        acc + (t?.returnQty != null ? Number(t.returnQty) : 0),
                    0
                );
                const damagedSum = stateForLine.reduce(
                    (acc: number, t: any) =>
                        acc + (t?.damagedQty != null ? Number(t.damagedQty) : 0),
                    0
                );
                const current = next[line.handoverLineId] || {};
                if (
                    current.utilized !== utilizedSum ||
                    current.returned !== returnedSum ||
                    current.damaged !== damagedSum
                ) {
                    next[line.handoverLineId] = {
                        ...current,
                        utilized: utilizedSum,
                        returned: returnedSum,
                        damaged: damagedSum,
                    };
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [isEditMode, detailsData?.lines, lineTasks]);

    const fetchHandoverDetails = async (handoverId: any) => {
        setIsLoading(true);
        try {
            const response = await inventoryCreation.fetchHandOverById(handoverId);
            const { response: _response, status } = response;
            if (status) {
                const raw = _response.data;
                const normalizedData = {
                    header: {
                        handoverId: raw.handoverId,
                        handoverNumber: raw.handoverNumber,
                        issueNumber: raw.issueNumber,
                        siteId: raw.siteId,
                        siteName: raw.siteName,
                        contractorId: raw.contractorId,
                        contractorName: raw.contractorName,
                        createdAt: raw.createdAt,
                        createdUserName: raw.createdUserName,
                        issueStatus: raw.utilizationStatus,
                        handoverStatus: raw.handoverStatus,
                        isStrictApproval: raw.isStrictApproval ?? false,
                        segmentLevel: raw.segmentLevel,
                        siteStructureUnitName: raw.siteStructureUnitName,
                        blockName: raw.blockName,
                        towerBlockVillaLabel: raw.towerBlockVillaLabel,
                        floorNo: raw.floorNo,
                        siteStructureSubUnitName: raw.siteStructureSubUnitName,
                        flatLabel: raw.flatLabel,
                        flatName: raw.flatName,
                        locationName: raw.locationName,
                        locationLabel: raw.locationLabel,
                        approvalRequired: raw.approvalRequired,
                        handoverToName: raw.handoverToName,
                        utilizationStatus: raw.utilizationStatus,
                        handoverDate: raw.handoverDate,
                    },
                    lines: raw.handoverLines || [],
                    approvals: raw.approvals || [],
                    dms: raw.dms || [],
                };

                const isStrict = normalizedData?.header?.isStrictApproval === true || normalizedData?.header?.isStrictApproval === 1;
                setIsStrictMode(isStrict);
                setDetailsData(normalizedData);

                if (normalizedData?.approvals) {
                    const processedApprovals = normalizedData.approvals.map((approval: any) => ({
                        ...approval,
                        level: approval.level?.toLowerCase() || approval.level,
                        status: approval.status?.toLowerCase() || approval.status,
                    }));

                    const ids = processedApprovals
                        .filter((item: any) => item?.status !== "approved")
                        .map((item: any) => item?.userId);

                    setApproverIds(ids);
                }
            }
        } catch (error) {
            console.error("Error fetching handover details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUploadClick = (handoverLineId: number, moduleFileType: string) => {
        setSelectedUploadData({
            handoverLineId,
            moduleFileType,
            uploadedFiles: getUploadedFiles(handoverLineId, moduleFileType)
        });
        setUploadModalVisible(true);
    };

    const handleFilesUpload = (
        files: File[],
        handoverLineId: number,
        moduleFileType: "damaged" | "returned"
    ) => {
        setSelectedFiles((prev) => [
            ...prev,
            ...files.map((file) => ({
                file,
                handoverLineId,
                moduleFileType,
            })),
        ]);
    };


    const getColumns = () => {
        const baseColumns: TableColumnsType<any> = [
            {
                title: "Material Category",
                dataIndex: "categoryName",
                key: "categoryName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Material Name",
                dataIndex: "materialName",
                key: "materialName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "UOM",
                dataIndex: "uomName",
                key: "uomName",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Utilization Status",
                dataIndex: "utilizationStatus",
                key: "utilizationStatus",
                width: 200,
                render: (text) => text || "NA",
            },
            {
                title: "Handovered Quantity",
                dataIndex: "quantityHandovered",
                key: "quantityHandovered",
                width: 200,
                render: (text) => <span>{text || "NA"}</span>,
            },
            {
                title: "Utilized Quantity",
                dataIndex: "quantityUtilized",
                key: "quantityUtilized",
                width: 220,
                render: (_, record) => {
                    const hasTaskLines =
                        Array.isArray(record?.materialHandoverTasks) &&
                        record.materialHandoverTasks.length > 0;
                    const taskSum = (lineTasks[record.handoverLineId] || []).reduce(
                        (acc: number, t: any) =>
                            acc + (t?.utilizedQty != null ? Number(t.utilizedQty) : 0),
                        0
                    );
                    const value = hasTaskLines
                        ? taskSum
                        : editedQuantities[record.handoverLineId]?.utilized ?? 0;

                    return isEditMode ? (
                        <InputNumber
                            min={0}
                            value={value}
                            style={{ width: "100%" }}
                            disabled={hasTaskLines}
                            onChange={(val) => {
                                setEditedQuantities((prev) => {
                                    const updated = {
                                        ...prev,
                                        [record.handoverLineId]: {
                                            ...prev[record.handoverLineId],
                                            utilized: Number(val || 0),
                                        },
                                    };

                                    const original = detailsData?.lines?.find(
                                        (l: any) => l.handoverLineId === record.handoverLineId
                                    );

                                    if (
                                        original &&
                                        (Number(val || 0) !== Number(original.quantityUtilized))
                                    ) {
                                        setHasChanges(true);
                                    }

                                    return updated;
                                });
                            }}
                        />
                    ) : (
                        <span>{record.quantityUtilized}</span>
                    );
                },
            },
            {
                title: "Returned Quantity",
                dataIndex: "quantityReturned",
                key: "quantityReturned",
                width: 220,
                render: (_, record) => {
                    const hasTaskLines =
                        Array.isArray(record?.materialHandoverTasks) &&
                        record.materialHandoverTasks.length > 0;
                    const taskSum = (lineTasks[record.handoverLineId] || []).reduce(
                        (acc: number, t: any) =>
                            acc + (t?.returnQty != null ? Number(t.returnQty) : 0),
                        0
                    );
                    const value = hasTaskLines
                        ? taskSum
                        : editedQuantities[record.handoverLineId]?.returned ?? 0;
                    const hasReturnedQty = value > 0 || record.quantityReturned > 0;
                    const uploadedFiles = [
                        ...getUploadedFiles(record.handoverLineId, "returned"),
                        ...getLocalUploadedFiles(record.handoverLineId, "returned"),
                    ];

                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {isEditMode ? (
                                <InputNumber
                                    min={0}
                                    value={value}
                                    disabled={hasTaskLines}
                                    onChange={(val) => {
                                        const newValue = val ?? 0;
                                        setEditedQuantities((prev) => ({
                                            ...prev,
                                            [record.handoverLineId]: {
                                                ...prev[record.handoverLineId],
                                                returned: Number(newValue),
                                            },
                                        }));
                                    }}
                                    style={{ flex: 1 }}
                                />
                            ) : (
                                <span style={{ flex: 1 }}>{record.quantityReturned}</span>
                            )}

                            {/* View uploaded files (dropdown only) */}
                            {uploadedFiles.length > 0 && (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <FileViewDropdown files={uploadedFiles} />

                                    {isEditMode &&
                                        uploadedFiles
                                            .filter((f: any) => f.__local)
                                            .map((file: any, i: number) => (
                                                <DeleteOutlined
                                                    key={i}
                                                    style={{ color: "red", cursor: "pointer" }}
                                                    onClick={() =>
                                                        handleDeleteLocalFile(
                                                            record.handoverLineId,
                                                            "returned",
                                                            file.fileName
                                                        )
                                                    }
                                                />
                                            ))}
                                </div>
                            )}

                            {/*  Upload (edit mode only) */}
                            {isEditMode && hasReturnedQty && (
                                <Tooltip title="Upload returned documents">
                                    <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() =>
                                            handleFileUploadClick(record.handoverLineId, "returned")
                                        }
                                    >
                                        <UploadOutlined />
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {
                title: "Damaged Quantity",
                dataIndex: "quantityDamaged",
                key: "quantityDamaged",
                width: 220,
                render: (_, record) => {
                    const hasTaskLines =
                        Array.isArray(record?.materialHandoverTasks) &&
                        record.materialHandoverTasks.length > 0;
                    const taskSum = (lineTasks[record.handoverLineId] || []).reduce(
                        (acc: number, t: any) =>
                            acc + (t?.damagedQty != null ? Number(t.damagedQty) : 0),
                        0
                    );
                    const value = hasTaskLines
                        ? taskSum
                        : editedQuantities[record.handoverLineId]?.damaged ?? 0;
                    const hasDamagedQty = value > 0 || record.quantityDamaged > 0;
                    const uploadedFiles = [
                        ...getUploadedFiles(record.handoverLineId, "damaged"),
                        ...getLocalUploadedFiles(record.handoverLineId, "damaged"),
                    ];

                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {isEditMode ? (
                                <InputNumber
                                    min={0}
                                    value={value}
                                    disabled={hasTaskLines}
                                    onChange={(val) => {
                                        const newValue = val ?? 0;
                                        setEditedQuantities((prev) => ({
                                            ...prev,
                                            [record.handoverLineId]: {
                                                ...prev[record.handoverLineId],
                                                damaged: Number(newValue),
                                            },
                                        }));
                                    }}
                                    style={{ flex: 1 }}
                                />
                            ) : (
                                <span style={{ flex: 1 }}>{record.quantityDamaged}</span>
                            )}

                            {/* View uploaded files (dropdown only) */}
                            {uploadedFiles.length > 0 && (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <FileViewDropdown files={uploadedFiles} />

                                    {isEditMode &&
                                        uploadedFiles
                                            .filter((f: any) => f.__local)
                                            .map((file: any, i: number) => (
                                                <DeleteOutlined
                                                    key={i}
                                                    style={{ color: "red", cursor: "pointer" }}
                                                    onClick={() =>
                                                        handleDeleteLocalFile(
                                                            record.handoverLineId,
                                                            "damaged",
                                                            file.fileName
                                                        )
                                                    }
                                                />
                                            ))}
                                </div>
                            )}

                            {/*  Upload (edit mode only) */}
                            {isEditMode && hasDamagedQty && (
                                <Tooltip title="Upload damaged documents">
                                    <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() =>
                                            handleFileUploadClick(record.handoverLineId, "damaged")
                                        }
                                    >
                                        <UploadOutlined />
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
        ];

        return baseColumns;
    };

    const handleUpdateHandover = () => {
        const initial: any = {};

        detailsData?.lines?.forEach((line: any) => {
            initial[line.handoverLineId] = {
                utilized: Number(line.quantityUtilized || 0),
                returned: Number(line.quantityReturned || 0),
                damaged: Number(line.quantityDamaged || 0),
            };
        });

        setEditedQuantities(initial);
        setHasChanges(false);
        setIsEditMode(true);
        setShowSubmitBtm(true);
    };

    const uploadedFileMap: Record<
        string,
        { returned: number; damaged: number }
    > = {};

    selectedFiles.forEach((item) => {
        const key = `${item.handoverLineId}`;
        if (!uploadedFileMap[key]) {
            uploadedFileMap[key] = { returned: 0, damaged: 0 };
        }
        uploadedFileMap[key][item.moduleFileType]++;
    });

    const getUploadedCount = (
        handoverLineId: number,
        moduleFileType: "returned" | "damaged"
    ) => {
        const apiCount =
            detailsData?.dms?.filter(
                (d: any) =>
                    d.handoverLineId === handoverLineId &&
                    d.moduleFileType === moduleFileType
            ).length || 0;

        const localCount = selectedFiles.filter(
            (f) =>
                f.handoverLineId === handoverLineId &&
                f.moduleFileType === moduleFileType
        ).length;

        return apiCount + localCount;
    };

    const handleSubmitChanges = async () => {
        const utilizationLines: any[] = [];
        let hasMissingDocs = false;
        let hasQtyMismatch = false;
        let hasTaskQtyMismatch = false;

        const docKeys = selectedFiles.map((item, index) => ({
            order: index,
            module: "utilization",
            moduleFileType: item.moduleFileType,
            handoverLineId: item.handoverLineId,
            handoverId: Number(id),
        }));

        detailsData?.lines?.forEach((line: any) => {
            const edited = editedQuantities[line.handoverLineId];
            if (!edited) return;

            const total =
                edited.utilized + edited.returned + edited.damaged;

            const handoverQty = Number(
                line.quantityHandovered ?? line.handoverQty ?? 0
            );

            const returnedFiles = getUploadedCount(
                line.handoverLineId,
                "returned"
            );
            const damagedFiles = getUploadedCount(
                line.handoverLineId,
                "damaged"
            );

            if (
                (edited.returned > 0 && returnedFiles === 0) ||
                (edited.damaged > 0 && damagedFiles === 0)
            ) {
                hasMissingDocs = true;
            }

            if (total > handoverQty) {
                hasQtyMismatch = true;
            }

            const handoverTaskSources: any[] = Array.isArray(line?.materialHandoverTasks)
                ? line.materialHandoverTasks
                : [];
            const tasksForLine = (lineTasks[line.handoverLineId] || [])
                .map((t: any) => {
                    const source = handoverTaskSources.find(
                        (s: any) => s.handoverTaskId === t.handoverTaskId
                    );
                    return {
                        handoverTaskId: Number(t.handoverTaskId),
                        taskId: source?.taskId != null ? Number(source.taskId) : null,
                        taskBoqId:
                            source?.taskBoqId != null ? Number(source.taskBoqId) : null,
                        utilizedQty:
                            t.utilizedQty != null && t.utilizedQty !== ""
                                ? Number(t.utilizedQty)
                                : 0,
                        returnQty:
                            t.returnQty != null && t.returnQty !== ""
                                ? Number(t.returnQty)
                                : 0,
                        damagedQty:
                            t.damagedQty != null && t.damagedQty !== ""
                                ? Number(t.damagedQty)
                                : 0,
                    };
                })
                .filter(
                    (t: any) =>
                        t.handoverTaskId != null &&
                        (t.utilizedQty > 0 || t.returnQty > 0 || t.damagedQty > 0)
                );

            const lineEntry: any = {
                handoverLineId: line.handoverLineId,
                utilizedQty: edited.utilized.toString(),
                returnQty: edited.returned.toString(),
                damagedQty: edited.damaged.toString(),
            };
            if (tasksForLine.length > 0) {
                const taskUtilizedSum = tasksForLine.reduce(
                    (sum: number, t: any) => sum + (t.utilizedQty || 0),
                    0
                );
                const taskReturnSum = tasksForLine.reduce(
                    (sum: number, t: any) => sum + (t.returnQty || 0),
                    0
                );
                const taskDamagedSum = tasksForLine.reduce(
                    (sum: number, t: any) => sum + (t.damagedQty || 0),
                    0
                );
                if (
                    taskUtilizedSum !== Number(edited.utilized) ||
                    taskReturnSum !== Number(edited.returned) ||
                    taskDamagedSum !== Number(edited.damaged)
                ) {
                    hasTaskQtyMismatch = true;
                }
                lineEntry.tasks = tasksForLine;
            }
            utilizationLines.push(lineEntry);
        });

        if (hasMissingDocs || hasQtyMismatch || hasTaskQtyMismatch) {
            const messages: string[] = [];

            if (hasMissingDocs) {
                messages.push(
                    "Returned/Damaged quantity entered but supporting documents are missing."
                );
            }

            if (hasQtyMismatch) {
                messages.push(
                    "Utilized + Returned + Damaged cannot exceed Handovered Quantity for any line."
                );
            }

            if (hasTaskQtyMismatch) {
                messages.push(
                    "Task-wise Utilized / Returned / Damaged quantities must sum to the line totals."
                );
            }

            Swal.fire({
                title: "Validation Error",
                html: messages.map(m => `• ${m}`).join("<br/>"),
                icon: "error",
            });

            setDisableSubmit(false);
            return;
        }

        const confirm = await Swal.fire({
            title: "Are you sure?",
            text: "Do you want to submit handover changes?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Submit",
            cancelButtonText: "Cancel",
        });

        if (!confirm.isConfirmed) return;

        const payload = {
            handOverId: Number(id),
            utilizationLines,
            docKeys,
            utilizationComments: "",
        };

        const formData = new FormData();
        formData.append("utilizationData", JSON.stringify(payload));

        selectedFiles.forEach(({ file }) => {
            formData.append("files", file);
        });

        setDisableSubmit(true);

        try {
            const res = await inventoryCreation.updateUtilizeQuantities(formData);
            if (res?.status) {
                Swal.fire("Success", "Utilization updated successfully", "success");
                setIsEditMode(false);
                setShowSubmitBtm(false);
                setEditedQuantities({});
                setSelectedFiles([]);
                fetchHandoverDetails(id);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: res?.message || "Something went wrong",
                });
            }
        } finally {
            setDisableSubmit(false);
        }
    };

    const handleRevertChanges = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to revert the changes?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, revert it!",
            cancelButtonText: "No, cancel",
        }).then(async (result) => {
            if (result.isConfirmed) {
                await fetchHandoverDetails(id);
                setIsEditMode(false);
                setShowSubmitBtm(false);
                setHasChanges(false);
                setEditedQuantities({});
                Swal.fire("Reverted!", "Your changes have been reverted.", "success");
            }
        });
    };

    const handoverDataForPDF = () => {
        return {
            header: detailsData?.header,
            lines: detailsData?.lines,
            approvals: detailsData?.approvals,
            signature: signatureFromApi?.filePath || null,
            orgName: detailsData?.orgName || "",
            orgAddress: detailsData?.orgAddress || "",
            orgCIN: detailsData?.orgCIN || "",
            orgGSTIN: detailsData?.orgGSTIN || "",
        };
    };

    return (
        <>
            <Head title="Handover Details" />
            <Content>
                <Card className="card-bordered">
                    <div className="card-aside-wrap">
                        {isLoading ? (
                            <Flex align="center" gap="middle" className="pageLoader">
                                <Spin size="large" className="m-auto" />
                            </Flex>
                        ) : (
                            <div className="card-inner card-inner-lg">
                                <BlockHead size="lg">
                                    <BlockBetween>
                                        <BlockHeadContent>
                                            <BlockTitle tag="h4">
                                                <span>Handover Details # - {detailsData?.header?.handoverNumber} | {detailsData?.header?.utilizationStatus}</span>
                                            </BlockTitle>
                                        </BlockHeadContent>
                                        <div className="d-flex">
                                            {canUpdateHandover && showSubmitBtm ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="actions clearfix paddingLeft10">
                                                        <ul className="m-auto d-flex align-items-center justify-content-center p-0" style={{ flexDirection: "row-reverse" }}>
                                                            <li className="pt-0 pb-0">
                                                                <Button
                                                                    color="primary"
                                                                    type="button"
                                                                    disabled={disableSubmit}
                                                                    onClick={handleSubmitChanges}
                                                                >
                                                                    Save Changes
                                                                </Button>
                                                            </li>
                                                            <li className="p-0">
                                                                {hasChanges && (
                                                                    <Button
                                                                        color="secondary"
                                                                        onClick={handleRevertChanges}
                                                                        type="button"
                                                                        className="btn-dim btn btn-secondary"
                                                                    >
                                                                        Revert Changes
                                                                    </Button>
                                                                )}
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    <div className="paddingLeft10">
                                                        <Button className="toggle" color="primary"
                                                            // onClick={() => navigate("/material-handover-list")}
                                                            onClick={() => navigate(fromList ? "/material-handover-list" : "/material-utilization")}
                                                        >
                                                            <Icon name="arrow-left" />
                                                            <span>Back</span>
                                                        </Button>
                                                    </div>

                                                    {/* PDF Button */}
                                                    <div className="paddingLeft10">
                                                        <MaterialHandoverPDF
                                                            handoverData={handoverDataForPDF()}
                                                            buyerOrgLogo={currentUser?.orgLogo}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center">
                                                    <div className="paddingLeft10">
                                                        <Button className="toggle" color="primary"
                                                            // onClick={() => navigate("/material-handover-list")}
                                                            onClick={() => navigate(fromList ? "/material-handover-list" : "/material-utilization")}
                                                        >
                                                            <Icon name="arrow-left" />
                                                            <span>Back</span>
                                                        </Button>
                                                    </div>

                                                    <div className="paddingLeft10">
                                                        <MaterialHandoverPDF
                                                            handoverData={handoverDataForPDF()}
                                                            buyerOrgLogo={currentUser?.orgLogo}
                                                        />
                                                    </div>
                                                    
                                                    {canUpdateHandover && !showSubmitBtm && (
                                                        <ul className="nk-tb-actions ms-2">
                                                            <li>
                                                                <UncontrolledDropdown>
                                                                    <DropdownToggle tag="a" className="dropdown-toggle btn btn-icon btn-trigger">
                                                                        <Icon name="more-v"></Icon>
                                                                    </DropdownToggle>
                                                                    <DropdownMenu end>
                                                                        <ul className="link-list-opt no-bdr">
                                                                            <li onClick={(e) => { e.preventDefault(); handleUpdateHandover(); }}>
                                                                                <Link to={`#`}>
                                                                                    <Icon name="edit" /> Update Utilization
                                                                                </Link>
                                                                            </li>
                                                                        </ul>
                                                                    </DropdownMenu>
                                                                </UncontrolledDropdown>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </BlockBetween>
                                </BlockHead>
                                <div className="content clearfix">
                                    <AntCollapse defaultActiveKey={["1", "2"]}>
                                        <Panel header="General Information" key="1">
                                            <div>
                                                <Col className="justify-start w-100 flex-wrap p-3">
                                                    <div className="w-50 border-end">
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Site</div>
                                                            <div className="data-value w-70">{detailsData?.header?.siteName || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Contractor</div>
                                                            <div className="data-value w-70">{detailsData?.header?.contractorName || "NA"}</div>
                                                        </div>
                                                    </div>
                                                    <div className="w-50 paddingLeft10">
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Utilization Status</div>
                                                            <div className="data-value w-70">{detailsData?.header?.utilizationStatus || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Issue Number</div>
                                                            <div className="data-value w-70">{detailsData?.header?.issueNumber || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Handover Number</div>
                                                            <div className="data-value w-70">{detailsData?.header?.handoverNumber || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Handover To</div>
                                                            <div className="data-value w-70">{detailsData?.header?.handoverToName || "NA"}</div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Creation Date</div>
                                                            <div className="data-value w-70">
                                                                {dayjs(detailsData?.header?.createdAt).format("DD-MMM-YYYY") || "NA"}
                                                            </div>
                                                        </div>
                                                        <div className="data-col align-center pt-1">
                                                            <div className="data-label w-30">Created By</div>
                                                            <div className="data-value w-70">{detailsData?.header?.createdUserName || "NA"}</div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </div>
                                        </Panel>
                                        <Panel header="Material Information" key="2">
                                            <div className="table-responsive poTable">
                                                <div style={{ margin: "0px auto 15px auto" }}>
                                                    <Table
                                                        className="customTable"
                                                        columns={getColumns()}
                                                        dataSource={detailsData?.lines?.map((item: any) => ({ ...item, key: item?.issueLineId || item?.handoverLineId }))}
                                                        scroll={{ x: 1000 }}
                                                        bordered
                                                        size="middle"
                                                        pagination={false}
                                                        expandable={{
                                                            expandedRowRender: (record: any) => {
                                                                const lineKey = record.handoverLineId;
                                                                const editable =
                                                                    canUpdateHandover && isEditMode;
                                                                const handoverTasks: any[] = Array.isArray(
                                                                    record?.materialHandoverTasks
                                                                )
                                                                    ? record.materialHandoverTasks
                                                                    : [];
                                                                if (handoverTasks.length === 0) {
                                                                    return (
                                                                        <div style={{ padding: "12px 16px", color: "#8c8c8c", fontStyle: "italic" }}>
                                                                            No tasks for this line.
                                                                        </div>
                                                                    );
                                                                }
                                                                const stateTasks = lineTasks[lineKey] || [];
                                                                const options =
                                                                    lineHandoverTaskOptions[lineKey] || [];
                                                                const resolveTaskName = (row: any) => {
                                                                    const stateMatch = stateTasks.find(
                                                                        (t: any) => t.handoverTaskId === row.handoverTaskId
                                                                    );
                                                                    if (stateMatch?.taskName) return stateMatch.taskName;
                                                                    const optMatch = options.find(
                                                                        (o: any) => o.handoverTaskId === row.handoverTaskId
                                                                    );
                                                                    if (optMatch?.taskName) return optMatch.taskName;
                                                                    return row?.taskId != null ? `Task #${row.taskId}` : "--";
                                                                };
                                                                const formatNum = (val: any) => {
                                                                    if (val == null || val === "") return "--";
                                                                    const n = Number(val);
                                                                    return Number.isNaN(n) ? val : n.toLocaleString("en-IN");
                                                                };
                                                                const lineHandoveredQty = Number(record?.quantityHandovered || 0);
                                                                const taskColumns = [
                                                                    {
                                                                        title: "#",
                                                                        key: "index",
                                                                        width: 44,
                                                                        align: "center" as const,
                                                                        render: (_: any, __: any, idx: number) => idx + 1,
                                                                    },
                                                                    {
                                                                        title: "Task",
                                                                        key: "taskName",
                                                                        render: (_: any, row: any) => resolveTaskName(row),
                                                                    },
                                                                    {
                                                                        title: "BOQ Line",
                                                                        dataIndex: "boqLineName",
                                                                        key: "boqLineName",
                                                                        render: (val: any) => val || "--",
                                                                    },
                                                                    {
                                                                        title: "Utilization Status",
                                                                        dataIndex: "status",
                                                                        key: "status",
                                                                        render: (val: any) => val || "--",
                                                                    },
                                                                    {
                                                                        title: "Handovered Quantity",
                                                                        dataIndex: "quantityHandovered",
                                                                        key: "quantityHandovered",
                                                                        align: "right" as const,
                                                                        render: (val: any) => formatNum(val),
                                                                    },
                                                                    {
                                                                        title: "Utilized Quantity",
                                                                        key: "quantityUtilized",
                                                                        width: 160,
                                                                        render: (_: any, row: any) => {
                                                                            const stateEntry = stateTasks.find(
                                                                                (t: any) => t.handoverTaskId === row.handoverTaskId
                                                                            );
                                                                            if (!editable) return formatNum(row?.quantityUtilized);
                                                                            const taskHandoveredQty = Number(row?.quantityHandovered || 0);
                                                                            return (
                                                                                <InputNumber
                                                                                    min={0}
                                                                                    step={0.001}
                                                                                    placeholder="0"
                                                                                    style={{ width: "100%" }}
                                                                                    value={stateEntry?.utilizedQty}
                                                                                    onChange={(val) => {
                                                                                        const numVal = val == null ? undefined : Number(val);
                                                                                        if (numVal != null && taskHandoveredQty > 0) {
                                                                                            const otherSum = (Number(stateEntry?.returnQty) || 0) + (Number(stateEntry?.damagedQty) || 0);
                                                                                            if (numVal + otherSum > taskHandoveredQty) {
                                                                                                Swal.fire({
                                                                                                    icon: "warning",
                                                                                                    title: "Invalid Quantity",
                                                                                                    text: `Utilized + Returned + Damaged (${numVal + otherSum}) cannot exceed Task Handovered Qty (${taskHandoveredQty}).`,
                                                                                                });
                                                                                                return;
                                                                                            }
                                                                                        }
                                                                                        if (stateEntry) {
                                                                                            updateTask(lineKey, stateEntry.key, { utilizedQty: numVal });
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            );
                                                                        },
                                                                    },
                                                                    {
                                                                        title: "Returned Quantity",
                                                                        key: "quantityReturned",
                                                                        width: 160,
                                                                        render: (_: any, row: any) => {
                                                                            const stateEntry = stateTasks.find(
                                                                                (t: any) => t.handoverTaskId === row.handoverTaskId
                                                                            );
                                                                            if (!editable) return formatNum(row?.quantityReturned);
                                                                            const taskHandoveredQty = Number(row?.quantityHandovered || 0);
                                                                            return (
                                                                                <InputNumber
                                                                                    min={0}
                                                                                    step={0.001}
                                                                                    placeholder="0"
                                                                                    style={{ width: "100%" }}
                                                                                    value={stateEntry?.returnQty}
                                                                                    onChange={(val) => {
                                                                                        const numVal = val == null ? undefined : Number(val);
                                                                                        if (numVal != null && taskHandoveredQty > 0) {
                                                                                            const otherSum = (Number(stateEntry?.utilizedQty) || 0) + (Number(stateEntry?.damagedQty) || 0);
                                                                                            if (numVal + otherSum > taskHandoveredQty) {
                                                                                                Swal.fire({
                                                                                                    icon: "warning",
                                                                                                    title: "Invalid Quantity",
                                                                                                    text: `Utilized + Returned + Damaged (${numVal + otherSum}) cannot exceed Task Handovered Qty (${taskHandoveredQty}).`,
                                                                                                });
                                                                                                return;
                                                                                            }
                                                                                        }
                                                                                        if (stateEntry) {
                                                                                            updateTask(lineKey, stateEntry.key, { returnQty: numVal });
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            );
                                                                        },
                                                                    },
                                                                    {
                                                                        title: "Damaged Quantity",
                                                                        key: "quantityDamaged",
                                                                        width: 160,
                                                                        render: (_: any, row: any) => {
                                                                            const stateEntry = stateTasks.find(
                                                                                (t: any) => t.handoverTaskId === row.handoverTaskId
                                                                            );
                                                                            if (!editable) return formatNum(row?.quantityDamaged);
                                                                            const taskHandoveredQty = Number(row?.quantityHandovered || 0);
                                                                            return (
                                                                                <InputNumber
                                                                                    min={0}
                                                                                    step={0.001}
                                                                                    placeholder="0"
                                                                                    style={{ width: "100%" }}
                                                                                    value={stateEntry?.damagedQty}
                                                                                    onChange={(val) => {
                                                                                        const numVal = val == null ? undefined : Number(val);
                                                                                        if (numVal != null && taskHandoveredQty > 0) {
                                                                                            const otherSum = (Number(stateEntry?.utilizedQty) || 0) + (Number(stateEntry?.returnQty) || 0);
                                                                                            if (numVal + otherSum > taskHandoveredQty) {
                                                                                                Swal.fire({
                                                                                                    icon: "warning",
                                                                                                    title: "Invalid Quantity",
                                                                                                    text: `Utilized + Returned + Damaged (${numVal + otherSum}) cannot exceed Task Handovered Qty (${taskHandoveredQty}).`,
                                                                                                });
                                                                                                return;
                                                                                            }
                                                                                        }
                                                                                        if (stateEntry) {
                                                                                            updateTask(lineKey, stateEntry.key, { damagedQty: numVal });
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            );
                                                                        },
                                                                    },
                                                                ];
                                                                return (
                                                                    <div style={{ padding: "4px 8px 12px" }}>
                                                                        <div style={{ fontWeight: 600, marginBottom: 12 }}>
                                                                            Task-wise Utilization
                                                                        </div>
                                                                        <Table
                                                                            columns={taskColumns}
                                                                            dataSource={handoverTasks}
                                                                            rowKey={(row: any) => row.handoverTaskId}
                                                                            pagination={false}
                                                                            size="small"
                                                                            bordered
                                                                            tableLayout="fixed"
                                                                            style={{ background: "#fff", borderRadius: 4 }}
                                                                        />
                                                                    </div>
                                                                );
                                                            },
                                                            rowExpandable: () => true,
                                                        }}
                                                    />
                                                </div>
                                                {signatureFromApi && (
                                                    <Row className="m-4">
                                                        <Col className="text-end">
                                                            <div className="signatureView" style={{
                                                                display: 'inline-block',
                                                                textAlign: 'left',
                                                                padding: '15px',
                                                                borderRadius: '5px',
                                                                maxWidth: '300px'
                                                            }}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <strong style={{ marginBottom: '10px' }}>Signature</strong>
                                                                    <img
                                                                        src={signatureFromApi.filePath}
                                                                        alt="Signature"
                                                                        style={{
                                                                            width: "160px",
                                                                            height: "100px",
                                                                            objectFit: 'contain',
                                                                            borderRadius: '4px'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </div>
                                        </Panel>
                                    </AntCollapse>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </Content>

            {/* File Upload Modal */}
            {uploadModalVisible && selectedUploadData && (
                <FileUploadModal
                    isVisible={uploadModalVisible}
                    onClose={() => {
                        setUploadModalVisible(false);
                        setSelectedUploadData(null);
                    }}
                    handoverLineId={selectedUploadData.handoverLineId}
                    moduleFileType={selectedUploadData.moduleFileType}
                    uploadedFiles={selectedUploadData.uploadedFiles}
                    onFilesUpload={handleFilesUpload}
                />
            )}
        </>
    );
};

export default MaterialHandoverListDetails;